import { useEffect, useMemo, useState, memo, useCallback } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { formatMoney, formatPercent } from "@/lib/prefs";
import { UNIT_KINDS } from "@/lib/units";
import { Plus, Pencil, Trash2, Loader2, Boxes, Upload } from "lucide-react";
import { toast } from "sonner";
import { formatApiErrorDetail } from "@/lib/api";
import { AllergenPicker } from "@/components/AllergenBadges";
import { InvoiceUploadTrigger } from "@/components/InvoiceUpload";
import BulkPriceUpdate from "@/components/BulkPriceUpdate";
import BulkIngredientsImportDialog from "@/components/BulkIngredientsImportDialog";
import EmptyState from "@/components/EmptyState";
import { SkeletonTableRow } from "@/components/GlassSkeleton";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import { useModuleTour, TourButton } from "@/components/useModuleTour";
import HelpTip from "@/components/HelpTip";

const emptyForm = {
  name: "",
  category: "food",
  purchase_qty: 1,
  purchase_unit: "kg",
  purchase_unit_kind: "weight",
  purchase_price: 0,
  yield_percent: 100,
  prep_time_minutes: 0,
  labor_rate_per_hour: 25,
  density_g_per_ml: "",
  allergens: [],
};

export function IngredientForm({ open, onOpenChange, initial, onSaved }) {
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [prediction, setPrediction] = useState(null);
  const [predictionDismissed, setPredictionDismissed] = useState(false);
  const isEdit = !!initial?.id;

  useEffect(() => {
    if (open) {
      setForm(initial ? { ...emptyForm, ...initial, allergens: initial.allergens || [], density_g_per_ml: initial.density_g_per_ml ?? "" } : emptyForm);
      setPrediction(null);
      setPredictionDismissed(false);
    }
  }, [open, initial]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const setKind = (kind) => {
    const first = UNIT_KINDS[kind][0];
    setForm((f) => ({ ...f, purchase_unit_kind: kind, purchase_unit: first }));
  };

  // Predictive Entry: as the operator types the ingredient name, hit the
  // Culinaire Ingredient Knowledge Base and offer a one-tap defaults chip.
  // Only fires for new ingredients (not on edit), never fires when the
  // operator has dismissed the chip for this session, debounced by 350ms.
  useEffect(() => {
    if (isEdit || predictionDismissed) { setPrediction(null); return; }
    const q = (form.name || "").trim();
    if (q.length < 3) { setPrediction(null); return; }
    let cancelled = false;
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get(`/corporate-chef/predict-entry`, { params: { name: q } });
        if (cancelled) return;
        if (data?.match) setPrediction(data);
        else setPrediction(null);
      } catch {
        if (!cancelled) setPrediction(null);
      }
    }, 350);
    return () => { cancelled = true; clearTimeout(t); };
  }, [form.name, isEdit, predictionDismissed]);

  const applyPrediction = () => {
    if (!prediction) return;
    setForm((f) => ({
      ...f,
      category: prediction.category || f.category,
      purchase_unit_kind: prediction.purchase_unit_kind || f.purchase_unit_kind,
      purchase_unit: prediction.purchase_unit || f.purchase_unit,
      yield_percent: prediction.yield_percent ?? f.yield_percent,
      density_g_per_ml: prediction.density_g_per_ml ?? f.density_g_per_ml,
    }));
    toast.success(`Applied Chef defaults for ${prediction.canonical}.`);
    setPredictionDismissed(true);
    setPrediction(null);
  };

  const submit = async () => {
    setSaving(true);
    try {
      const payload = {
        ...form,
        purchase_qty: Number(form.purchase_qty),
        purchase_price: Number(form.purchase_price),
        yield_percent: Number(form.yield_percent),
        prep_time_minutes: Number(form.prep_time_minutes),
        labor_rate_per_hour: Number(form.labor_rate_per_hour),
        density_g_per_ml: form.density_g_per_ml === "" ? null : Number(form.density_g_per_ml),
      };
      let saved;
      if (isEdit) {
        const res = await api.patch(`/ingredients/${initial.id}`, payload);
        saved = res.data;
      } else {
        const res = await api.post("/ingredients", payload);
        saved = res.data;
      }
      // Hand the parent both the saved row and (for edits) the snapshot
      // the row had *before* the change so the Undo toast can restore it.
      onSaved(saved, isEdit ? initial : null);
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="ingredient-form-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">
            {isEdit ? "Edit ingredient" : "New ingredient"}
          </DialogTitle>
          <DialogDescription className="text-xs text-muted-foreground">
            {isEdit ? "Update pricing, yield or unit for this pantry item." : "Add a pantry item so recipes and inventory can cost against it."}
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2">
            <Label>Name</Label>
            <Input value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="ing-name" />
            {prediction && (
              <div
                className="mt-2 rounded-xl flex items-start gap-2.5 p-2.5 text-xs"
                style={{
                  background: "linear-gradient(135deg, rgba(212,175,55,0.10), rgba(232,207,154,0.04))",
                  border: "1px solid rgba(212,175,55,0.45)",
                  boxShadow: "0 0 18px rgba(212,175,55,0.10), inset 0 1px 0 rgba(255,255,255,0.06)",
                }}
                data-testid="predictive-entry-chip"
              >
                <div
                  className="shrink-0 mt-0.5 w-4 h-4 rounded-full flex items-center justify-center"
                  style={{ background: "radial-gradient(circle at 30% 30%, rgba(255,248,225,0.9), rgba(212,175,55,0.35))" }}
                />
                <div className="flex-1 leading-relaxed text-white/85">
                  <span className="uppercase tracking-[0.18em] text-[10px] text-[#d4af37] font-medium">Chef defaults</span>
                  <div className="mt-0.5">
                    <span className="font-medium text-white/95">{prediction.canonical}:</span>{" "}
                    {prediction.purchase_unit_kind} in {prediction.purchase_unit}, {prediction.yield_percent}% yield
                    {prediction.density_g_per_ml ? `, density ${prediction.density_g_per_ml} g per ml` : ""}.
                  </div>
                </div>
                <button
                  type="button"
                  onClick={applyPrediction}
                  className="shrink-0 px-2.5 py-1 rounded-lg text-[11px] font-medium"
                  style={{
                    background: "linear-gradient(180deg, rgba(232,207,154,0.35), rgba(200,169,106,0.14))",
                    border: "1px solid rgba(232,207,154,0.55)",
                    color: "#faf3df",
                    cursor: "pointer",
                  }}
                  data-testid="predictive-entry-apply"
                >
                  Apply
                </button>
                <button
                  type="button"
                  onClick={() => { setPrediction(null); setPredictionDismissed(true); }}
                  className="shrink-0 w-6 h-6 rounded-full text-white/50 hover:text-white/80"
                  style={{ background: "transparent", border: "1px solid rgba(255,255,255,0.10)", cursor: "pointer" }}
                  aria-label="Dismiss suggestion"
                  data-testid="predictive-entry-dismiss"
                >
                  ×
                </button>
              </div>
            )}
          </div>
          <div>
            <Label>Category</Label>
            <Select value={form.category} onValueChange={(v) => set("category", v)}>
              <SelectTrigger data-testid="ing-category"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="food">Food</SelectItem>
                <SelectItem value="drink">Drink</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Unit kind</Label>
            <Select value={form.purchase_unit_kind} onValueChange={setKind}>
              <SelectTrigger data-testid="ing-unit-kind"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="weight">Weight</SelectItem>
                <SelectItem value="volume">Volume</SelectItem>
                <SelectItem value="count">Count</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Pack quantity</Label>
            <Input
              type="number"
              step="0.01"
              value={form.purchase_qty}
              onChange={(e) => set("purchase_qty", e.target.value)}
              data-testid="ing-qty"
            />
          </div>
          <div>
            <Label>Pack unit</Label>
            <Select value={form.purchase_unit} onValueChange={(v) => set("purchase_unit", v)}>
              <SelectTrigger data-testid="ing-unit"><SelectValue /></SelectTrigger>
              <SelectContent>
                {UNIT_KINDS[form.purchase_unit_kind].map((u) => (
                  <SelectItem key={u} value={u}>{u}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="col-span-2">
            <Label>Pack price</Label>
            <Input
              type="number"
              step="0.01"
              value={form.purchase_price}
              onChange={(e) => set("purchase_price", e.target.value)}
              data-testid="ing-price"
            />
          </div>
          <div>
            <Label className="flex items-center gap-1.5">
              Edible yield %
              <HelpTip
                testId="help-yield-percent"
                text="How much of the raw purchase remains as edible product after trimming, peeling and prep loss. A carrot at 90% loses 10% to peel and tops."
              />
            </Label>
            <Input
              type="number"
              step="1"
              value={form.yield_percent}
              onChange={(e) => set("yield_percent", e.target.value)}
              data-testid="ing-yield"
            />
          </div>
          <div>
            <Label className="flex items-center gap-1.5">
              Prep time (min)
              <HelpTip
                testId="help-prep-time"
                text="Average minutes to trim, portion and mise en place this ingredient. We combine it with your labor rate to add the true prep cost onto each recipe."
              />
            </Label>
            <Input
              type="number"
              step="1"
              value={form.prep_time_minutes}
              onChange={(e) => set("prep_time_minutes", e.target.value)}
              data-testid="ing-prep"
            />
          </div>
          <div>
            <Label>Labor rate ($/hr)</Label>
            <Input
              type="number"
              step="0.5"
              value={form.labor_rate_per_hour}
              onChange={(e) => set("labor_rate_per_hour", e.target.value)}
              data-testid="ing-labor"
            />
          </div>
          <div>
            <Label>Density (g/ml, optional)</Label>
            <Input
              type="number"
              step="0.01"
              value={form.density_g_per_ml}
              onChange={(e) => set("density_g_per_ml", e.target.value)}
              placeholder="e.g. 0.91"
              data-testid="ing-density"
            />
          </div>
          <div className="col-span-2">
            <Label>Allergens</Label>
            <AllergenPicker value={form.allergens || []} onChange={(v) => set("allergens", v)} />
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={saving || !form.name} data-testid="ing-save">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// IngredientRow: extracted + memoized so typing in the search box (or
// editing another row) does not re-render every row on the screen. Only
// rows whose ingredient object identity changed, or whose edit/delete
// handlers changed, will re-render. Combined with the memoized handler
// refs in the parent this keeps large pantries (500+ items) buttery.
const IngredientRow = memo(function IngredientRow({ i, isSelected, onSelect, onEdit, onDelete }) {
  const perUnit = (() => {
    const usable = i.purchase_qty * (i.yield_percent / 100);
    return usable > 0 ? i.purchase_price / usable : 0;
  })();
  return (
    <tr
      className={`border-t border-border/60 cursor-pointer transition-colors ${
        isSelected ? "bg-[rgba(212,175,55,0.10)]" : "hover:bg-secondary/30"
      }`}
      onClick={() => onSelect(i)}
      data-testid={`ing-row-${i.id}`}
      data-selected={isSelected ? "true" : "false"}
    >
      <td className="px-4 py-3 font-medium">{i.name}</td>
      <td className="px-4 py-3 capitalize text-muted-foreground">{i.category}</td>
      <td className="px-4 py-3 text-right tabular">{i.purchase_qty} {i.purchase_unit}</td>
      <td className="px-4 py-3 text-right tabular">{formatMoney(i.purchase_price)}</td>
      <td className="px-4 py-3 text-right tabular">{formatPercent(i.yield_percent, 0)}</td>
      <td className="px-4 py-3 text-right tabular">
        {formatMoney(perUnit)}
        <span className="text-muted-foreground"> / {i.purchase_unit}</span>
      </td>
      <td className="px-4 py-3 text-right">
        <div className="inline-flex gap-1" onClick={(e) => e.stopPropagation()}>
          <Button
            variant="ghost" size="sm"
            onClick={() => onEdit(i)}
            data-testid={`ing-edit-${i.id}`}
          >
            <Pencil className="w-4 h-4" />
          </Button>
          <Button
            variant="ghost" size="sm"
            onClick={() => onDelete(i)}
            data-testid={`ing-delete-${i.id}`}
          >
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      </td>
    </tr>
  );
});

// IngredientCostPanel: iPad master-detail right pane. Shows the live
// costing breakdown for the currently selected ingredient. Empty state
// invites the operator to pick a row from the table on the left.
// On viewports below md (768px) this component is hidden entirely; the
// row action buttons remain the primary interaction on phones.
function IngredientCostPanel({ item, onEdit }) {
  if (!item) {
    return (
      <div
        className="rounded-2xl glass-card p-6 sticky top-24"
        style={{ minHeight: "24rem" }}
        data-testid="ing-detail-empty"
      >
        <div className="label-eyebrow text-muted-foreground">Master Detail</div>
        <h2 className="font-display text-2xl mt-2 leading-tight">Select an ingredient</h2>
        <p className="text-sm text-muted-foreground mt-3 leading-relaxed">
          Tap any row on the left to audit its pack size, usable cost per unit, and yield efficiency here. Every recipe using the selected ingredient recosts in real time.
        </p>
        <div className="mt-6 p-4 rounded-lg" style={{ background: "rgba(212,175,55,0.04)", border: "1px solid rgba(212,175,55,0.18)" }}>
          <div className="label-eyebrow text-[#d4af37]">iPad view</div>
          <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
            You are seeing the Master Detail layout because your device is 768px wide or more. On phones the list stays full width.
          </p>
        </div>
      </div>
    );
  }
  const usable = (item.purchase_qty || 0) * ((item.yield_percent || 0) / 100);
  const perUnit = usable > 0 ? item.purchase_price / usable : 0;
  const waste = (item.purchase_qty || 0) - usable;
  const wastePct = item.yield_percent ? (100 - item.yield_percent) : 0;
  return (
    <div className="rounded-2xl glass-card p-6 sticky top-24 space-y-5" data-testid="ing-detail-panel">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="label-eyebrow capitalize">{item.category || "Ingredient"}</div>
          <h2 className="font-display text-2xl mt-1 leading-tight break-words">{item.name}</h2>
        </div>
        <Button
          size="sm"
          variant="outline"
          onClick={() => onEdit(item)}
          data-testid="ing-detail-edit"
          className="shrink-0"
        >
          <Pencil className="w-3.5 h-3.5 mr-1.5" /> Edit
        </Button>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div className="rounded-lg p-3.5" style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.08)" }}>
          <div className="label-eyebrow text-muted-foreground">Pack</div>
          <div className="font-display text-xl tabular mt-1">{item.purchase_qty} <span className="text-sm text-muted-foreground font-sans font-normal">{item.purchase_unit}</span></div>
        </div>
        <div className="rounded-lg p-3.5" style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.08)" }}>
          <div className="label-eyebrow text-muted-foreground">Pack price</div>
          <div className="font-display text-xl tabular mt-1">{formatMoney(item.purchase_price)}</div>
        </div>
        <div className="rounded-lg p-3.5" style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.08)" }}>
          <div className="label-eyebrow text-muted-foreground">Edible yield</div>
          <div className="font-display text-xl tabular mt-1">{formatPercent(item.yield_percent, 0)}</div>
        </div>
        <div className="rounded-lg p-3.5" style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.08)" }}>
          <div className="label-eyebrow text-muted-foreground">Waste</div>
          <div className="font-display text-xl tabular mt-1">{waste.toFixed(2)} <span className="text-sm text-muted-foreground font-sans font-normal">{item.purchase_unit}</span></div>
        </div>
      </div>

      <div className="rounded-lg p-4" style={{ background: "linear-gradient(135deg, rgba(212,175,55,0.10), rgba(232,207,154,0.03))", border: "1px solid rgba(212,175,55,0.35)" }}>
        <div className="label-eyebrow" style={{ color: "#d4af37" }}>Usable cost per unit</div>
        <div className="font-display text-3xl tabular mt-1.5" style={{ color: "#faf3df" }}>
          {formatMoney(perUnit)}
          <span className="text-base text-muted-foreground font-sans font-normal ml-1">/ {item.purchase_unit}</span>
        </div>
        <p className="text-[0.7rem] text-muted-foreground mt-2 leading-relaxed">
          The true cost after accounting for {wastePct.toFixed(0)}% trim loss. Every recipe using {item.name} bills at this rate.
        </p>
      </div>

      {item.density_g_per_ml ? (
        <div className="text-xs text-muted-foreground flex items-center gap-2">
          <span className="label-eyebrow">Density</span>
          <span className="tabular">{item.density_g_per_ml} g per ml</span>
        </div>
      ) : null}

      {item.allergens && item.allergens.length > 0 && (
        <div>
          <div className="label-eyebrow text-muted-foreground mb-1.5">Allergens</div>
          <div className="flex flex-wrap gap-1.5">
            {item.allergens.map((a) => (
              <span key={a} className="px-2 py-0.5 rounded-full text-[0.68rem] capitalize" style={{ background: "rgba(220,90,90,0.12)", border: "1px solid rgba(220,90,90,0.35)", color: "#f2c4c4" }}>
                {a}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default function Ingredients() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [query, setQuery] = useState("");
  // Master-detail: id of the currently selected ingredient. The right
  // pane on md+ viewports mirrors this row's cost breakdown. Auto-selects
  // the first row once the list loads so the panel is never empty by
  // default on iPad.
  const [selectedId, setSelectedId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/ingredients");
      setItems(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  // Undo helpers: each of add, edit, delete stashes the payload needed to
  // reverse itself and offers a toast action for 8 seconds. This is what
  // the user asked for: no confirm modals, no dead ends, one-click safety
  // net after every write. All three paths finish in a single round trip.
  const UNDO_MS = 8000;

  const undoCreate = async (created) => {
    try {
      await api.delete(`/ingredients/${created.id}`);
      toast.success(`Undone: ${created.name} removed`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const undoEdit = async (previous) => {
    try {
      const payload = {
        name: previous.name,
        category: previous.category,
        purchase_qty: Number(previous.purchase_qty),
        purchase_unit: previous.purchase_unit,
        purchase_unit_kind: previous.purchase_unit_kind,
        purchase_price: Number(previous.purchase_price),
        yield_percent: Number(previous.yield_percent),
        prep_time_minutes: Number(previous.prep_time_minutes),
        labor_rate_per_hour: Number(previous.labor_rate_per_hour),
        density_g_per_ml: previous.density_g_per_ml ?? null,
        allergens: previous.allergens || [],
      };
      await api.patch(`/ingredients/${previous.id}`, payload);
      toast.success(`Undone: ${previous.name} reverted`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const undoDelete = async (deletedDoc) => {
    try {
      const { id: _skip, created_at: _c, updated_at: _u, ...rest } = deletedDoc;
      const res = await api.post("/ingredients", rest);
      toast.success(`Undone: ${res.data.name} restored`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const handleSaved = (saved, previous) => {
    load();
    if (previous) {
      toast.success(`Updated: ${saved.name}`, {
        duration: UNDO_MS,
        action: {
          label: "Undo",
          onClick: () => undoEdit(previous),
        },
      });
    } else {
      toast.success(`Added: ${saved.name}`, {
        duration: UNDO_MS,
        action: {
          label: "Undo",
          onClick: () => undoCreate(saved),
        },
      });
    }
  };

  const remove = useCallback(async (item) => {
    // Optimistic delete: no confirm dialog, so add/edit/delete each finish
    // in a single click. The Undo action lives inside the toast for 8s.
    try {
      await api.delete(`/ingredients/${item.id}`);
      setItems((prev) => prev.filter((r) => r.id !== item.id));
      toast.success(`Deleted: ${item.name}`, {
        duration: UNDO_MS,
        action: {
          label: "Undo",
          onClick: () => undoDelete(item),
        },
      });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      // Ensure UI matches server state if the delete actually blocked.
      load();
    }
  }, []);

  // Debounce the search input by 200ms so typing does not re-filter on
  // every keystroke. Filter itself is wrapped in useMemo so it only runs
  // when items or the debounced query actually change.
  const [debouncedQuery] = useDebouncedValue(query, 200);
  const filtered = useMemo(() => {
    const q = debouncedQuery.toLowerCase();
    if (!q) return items;
    return items.filter((i) => i.name.toLowerCase().includes(q));
  }, [items, debouncedQuery]);

  // Auto-select the first filtered ingredient once the list is populated
  // (or after a search narrows the results). This guarantees the iPad
  // master-detail right pane is never empty when there is at least one
  // ingredient the operator can see. Selected id gets cleared if the
  // ingredient it points at is no longer in the filtered set.
  useEffect(() => {
    if (!filtered.length) { setSelectedId(null); return; }
    if (!filtered.some((i) => i.id === selectedId)) {
      setSelectedId(filtered[0].id);
    }
  }, [filtered, selectedId]);

  const selectedItem = useMemo(
    () => items.find((i) => i.id === selectedId) || null,
    [items, selectedId]
  );

  const handleSelect = useCallback((i) => setSelectedId(i.id), []);

  const handleEdit = useCallback((i) => {
    setEditing(i);
    setDialogOpen(true);
  }, []);

  const tourSteps = useMemo(() => ([
    {
      selector: '[data-testid="ing-search"]',
      title: "Find any ingredient",
      body: "Search by name. Long pantries load instantly because we debounce every keystroke.",
      placement: "bottom",
    },
    {
      selector: '[data-testid="ing-bulk-btn"]',
      title: "Bulk import",
      body: "Drop a CSV of your entire pantry. We match units, dial codes and yields automatically so you're up in minutes, not days.",
      placement: "bottom",
    },
    {
      selector: '[data-testid="ing-new"]',
      title: "New ingredient",
      body: "Add pack size, price and edible yield. Every recipe that uses it re-costs live.",
      placement: "bottom",
    },
  ]), []);
  const tour = useModuleTour("ingredients_v1", tourSteps);

  return (
    <div className="p-4 sm:p-6 lg:p-10 max-w-7xl mx-auto space-y-6" data-testid="ingredients-page">
      {tour.TourNode}
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="label-eyebrow flex items-center gap-2"><Boxes className="w-3 h-3" /> Pantry</div>
          <h1 className="font-display text-3xl md:text-4xl leading-none mt-2">Ingredients</h1>
          <p className="text-muted-foreground mt-2 text-sm max-w-md">
            How you buy each item: pack size, price, yield, prep. This is the base of every recipe cost.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <TourButton onClick={tour.start} testId="ing-tour-btn" />
          <Input
            placeholder="Search…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="max-w-xs"
            data-testid="ing-search"
          />
          <InvoiceUploadTrigger ingredients={items} onApplied={load} />
          <BulkPriceUpdate ingredients={items} onDone={load} />
          <Button variant="outline" className="gap-2" onClick={() => setBulkOpen(true)} data-testid="ing-bulk-btn">
            <Upload className="w-4 h-4" /> Bulk import
          </Button>
          <Button
            onClick={() => { setEditing(null); setDialogOpen(true); }}
            className="gap-2"
            data-testid="ing-new"
          >
            <Plus className="w-4 h-4" /> New
          </Button>
        </div>
      </header>

      {loading ? (
        <div className="rounded-2xl glass-card overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-secondary/50">
              <tr className="text-left">
                <th className="px-4 py-3 label-eyebrow font-bold">Name</th>
                <th className="px-4 py-3 label-eyebrow font-bold">Category</th>
                <th className="px-4 py-3 label-eyebrow font-bold text-right">Pack</th>
                <th className="px-4 py-3 label-eyebrow font-bold text-right">Pack price</th>
                <th className="px-4 py-3 label-eyebrow font-bold text-right">Yield</th>
                <th className="px-4 py-3 label-eyebrow font-bold text-right">Per unit (usable)</th>
                <th className="px-4 py-3 label-eyebrow font-bold text-right">Actions</th>
              </tr>
            </thead>
            <tbody data-testid="ing-skeleton">
              {Array.from({ length: 6 }).map((_, i) => <SkeletonTableRow key={i} cols={7} />)}
            </tbody>
          </table>
        </div>
      ) : items.length === 0 ? (
        <EmptyState
          illustration="knife_and_board"
          eyebrow="Empty pantry"
          title="Your kitchen starts here."
          body="Add your first ingredient to activate recipe costing, yield tracking, and automatic price sync from your suppliers. Thirty seconds per line."
          ctaLabel="Add your first ingredient"
          ctaIcon={Plus}
          onCtaClick={() => { setEditing(null); setDialogOpen(true); }}
          ctaTestId="empty-ing-add"
          secondaryLabel="Bulk import a CSV"
          onSecondaryClick={() => setBulkOpen(true)}
        />
      ) : (
        // Master-detail grid: single column below 768px (phones), 60/40
        // split on md and lg (iPad portrait + landscape under 1280px),
        // 3fr/2fr on xl+ so the right pane stays balanced against a wider
        // list on desktop. Gap is 24px per the expansive whitespace rule.
        <div className="grid grid-cols-1 md:grid-cols-[minmax(0,3fr)_minmax(0,2fr)] gap-6" data-testid="ing-master-detail">
          <div className="rounded-2xl glass-card overflow-hidden min-w-0">
            <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[720px]">
              <thead className="bg-secondary/50">
                <tr className="text-left">
                  <th className="px-4 py-3 label-eyebrow font-bold">Name</th>
                  <th className="px-4 py-3 label-eyebrow font-bold">Category</th>
                  <th className="px-4 py-3 label-eyebrow font-bold text-right">Pack</th>
                  <th className="px-4 py-3 label-eyebrow font-bold text-right">Pack price</th>
                  <th className="px-4 py-3 label-eyebrow font-bold text-right">Yield</th>
                  <th className="px-4 py-3 label-eyebrow font-bold text-right">Per unit (usable)</th>
                  <th className="px-4 py-3 label-eyebrow font-bold text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((i) => (
                  <IngredientRow
                    key={i.id}
                    i={i}
                    isSelected={i.id === selectedId}
                    onSelect={handleSelect}
                    onEdit={handleEdit}
                    onDelete={remove}
                  />
                ))}
                {!filtered.length && (
                  <tr>
                    <td colSpan={7} className="px-4 py-10 text-center text-muted-foreground">
                      No ingredients. Add your first one.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
            </div>
          </div>

          {/* Right pane: master-detail on tablet+ only. Hidden on phones
              where the row taps still open the modal edit flow. */}
          <div className="hidden md:block">
            <IngredientCostPanel item={selectedItem} onEdit={handleEdit} />
          </div>
        </div>
      )}

      <IngredientForm
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        initial={editing}
        onSaved={handleSaved}
      />
      <BulkIngredientsImportDialog
        open={bulkOpen}
        onOpenChange={setBulkOpen}
        onImported={load}
      />
    </div>
  );
}
